// 主治醫師合理醫療服務量設定整頁（customer-tsgh-service-standard-setting＝legacy
// customer/tsgh/serviceStandardSetting.jsp，/tsghfunc/toServiceStandardSetting，display 頁型）。
// 批次 17（B8-3，Gitea issue #16）Wave 2。掛在 PageBehavior.complexBody（DisplayPage 已補早退）。
//
// legacy 流程（行號親驗 2026-09-25，基準＝serviceStandardSetting.jsp＋TsghCustomerFuncController.java；
// 詳 docs/plans/2026-09-25-batch17-vote-setting-plan.md §1.6/§1.7）：
//   - ready（:17-27）：級聯（同 highRiskSetting）＋**dataList=JSON.parse('${settingList}')（server 固定
//     評估項目，settingType=0）**。
//   - initListTable（:69-108）3 欄：評估項目 itemDesc1／評估內容 a_itemDesc1 text input／本科標準：
//     radio `N/A`（chk_a_itemValue1_{id} value 0）＋radio 數值（value 1，**預設勾選** radsel ['','checked']）
//     ＋number a_itemValue1_{id}＋**addon＝row.itemDesc2（單位）**＋a_id 有值→hidden a_id_{id}。
//   - valueRadioChange（:55-68）：N/A→disable number＋內容輸入；數值→enable。
//   - doQuery（:111-131）：請選擇醫院及部門 gate；POST tsghfunc/getServiceStandardDeptSettingListAjax
//     {deptId}→**list 依 itemCode 合併入 ddList**（a_itemDesc1/a_itemValue1/a_id）。
//   - doSave（:134-164）：逐列（settingType '1'/itemCode/sort/isEnable '1'/erasable '1'/departmentId/
//     itemDesc1/**chk=0→itemValue1='-1' 否則 number 值**/a_id 有值才帶 id）→POST
//     tsghfunc/saveServiceStandardDeptSettingAjax→success：**tip(data.msg)＋if(data.success) doQuery()——
//     `tip` 全 WebContent 無定義＋server 回 {desc,status} 無 msg/success 欄＝存檔後靜默、不重查
//     【legacy bug 照實，D8】**。
//
// 雛形策略（執行文件 D 系列）：存檔＝update（endpoint-map update:customer-tsgh-service-standard-setting ✓）
// ＋dataEffect（C5-j）；**存檔後無訊息、不重查（照 legacy bug，D8）**；查詢＝list（✓ mapped；科部 select
// 觸發＝無按鈕可掛 data-effect，讀取端點 parity 不可見，登記）；固定評估項目＝SERVICE_STANDARD_ITEMS
// 常數（server settingList；【雛形值待實機校準】）；表格輸入不帶 name（D4）；存檔 payload 補帶
// organizationId/departmentId（legacy 只送 deptId；C5-h 接線，登記）＋datas 逐列；錨點列同 HighRiskSettingBody。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useEffect, useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Radio } from '@/components/base/Radio';
import { Select } from '@/components/base/Select';
import { Table, type TableRow } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { MockDataError, useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
  SERVICE_STANDARD_ITEMS,
} from '@/mocks/resource-rules';
import type { Row } from '@/mocks/types';

/** 訊息字面值（serviceStandardSetting.jsp＋TsghCustomerFuncController.java）。 */
const TEXT_NO_DEPT = '請選擇醫院及部門'; // :113
const TEXT_SAVE_ERROR = '更新資料失敗!'; // controller :201/:207（前端 tip bug 收不到；雛形錯誤路徑用）
const TEXT_QUERY_ERROR = '查詢資料失敗!'; // controller :181

/** 單一評估項目的科部設定值（legacy ddList 列的 a_* 欄）。 */
interface DeptValue {
  aItemDesc1: string;
  aValue1: string;
  aId: string;
  naChecked: boolean; // legacy radsel：預設數值（value 1）勾選；a_itemValue1=='-1'→N/A
}

const EMPTY_DEPT_VALUE: DeptValue = { aItemDesc1: '', aValue1: '', aId: '', naChecked: false };

export function ServiceStandardSettingBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  // ------------------------------------------------------------------
  // 級聯（ready 即查詢）＋固定評估項目＋科部設定值
  // ------------------------------------------------------------------
  const [organizationId, setOrganizationId] = useState('org-1');
  const [departmentId, setDepartmentId] = useState('dept-1');
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數（規格 §7.1）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);
  const [deptValues, setDeptValues] = useState<readonly DeptValue[]>(
    SERVICE_STANDARD_ITEMS.map(() => EMPTY_DEPT_VALUE),
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [anchorId, setAnchorId] = useState('');

  /** doQuery（:111-131）：gate＋list＋**依 itemCode 合併**（a_itemDesc1/a_itemValue1/a_id）。 */
  const doQuery = () => {
    void (async () => {
      if (departmentId === '' || Number.parseInt(departmentId, 10) < 0) {
        await dialogs.alert(TEXT_NO_DEPT);
        return;
      }
      setBusy(true);
      try {
        const result = await dataSource.list(pageId, {
          filters: { departmentId },
        });
        // :126-136 合併：response.list 依 itemCode 對應 ddList。
        const next = SERVICE_STANDARD_ITEMS.map((item, index) => {
          const base = deptValues[index] ?? EMPTY_DEPT_VALUE;
          const hit = result.rows.find(
            (row) => String(row['itemCode'] ?? '') === String(item['itemCode'] ?? ''),
          );
          if (hit === undefined) {
            return base;
          }
          const value1 = String(hit['itemValue1'] ?? '');
          return {
            aItemDesc1: String(hit['itemDesc1'] ?? ''),
            aValue1: value1 === '-1' ? '' : value1,
            aId: String(hit['id'] ?? ''),
            naChecked: value1 === '-1',
          };
        });
        setDeptValues(next);
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : TEXT_QUERY_ERROR);
      } finally {
        setBusy(false);
        setLoading(false);
      }
    })();
  };

  useEffect(() => {
    void (async () => {
      try {
        // 錨點列（resource 最後一列；設定存列上＝B1-2 先例）。
        const anchor = await dataSource.list(pageId, { page: 1, pageSize: 12 });
        setAnchorId(String(anchor.rows[anchor.rows.length - 1]?.id ?? ''));
      } catch {
        setAnchorId('');
      }
    })();
    doQuery(); // ready 即查詢（:26-27 departmentChangeEvent 鏡像）
    // pageId 一次掛載內不變（ComplexFormPage 同款註記）。
    // biome-ignore lint/correctness/useExhaustiveDependencies: 見上
  }, [pageId]);

  const patchValue = (index: number, patch: Partial<DeptValue>) => {
    setDeptValues((current) =>
      current.map((value, i) => (i === index ? { ...value, ...patch } : value)),
    );
  };

  // ------------------------------------------------------------------
  // 存檔（doSave :134-164）——legacy 靜默 bug 照實（D8）：無訊息、不重查。
  // ------------------------------------------------------------------
  const doSave = () => {
    void (async () => {
      const datas = SERVICE_STANDARD_ITEMS.map((item, index) => {
        const value = deptValues[index] ?? EMPTY_DEPT_VALUE;
        // :141-158：chk=0→itemValue1='-1'；否則 number 值；a_id 有值才帶 id。
        const data: Row = {
          settingType: '1',
          itemCode: String(item['itemCode'] ?? ''),
          sort: String(item['sort'] ?? '0'),
          isEnable: '1',
          erasable: '1',
          departmentId,
          itemDesc1: value.aItemDesc1,
          itemValue1: value.naChecked ? '-1' : value.aValue1,
        };
        if (value.aId !== '') {
          data['id'] = value.aId;
        }
        return data;
      });
      if (anchorId === '') {
        // 無錨點（錯誤 scenario）：legacy 靜默路徑無錯誤框，照實不送。
        return;
      }
      setBusy(true);
      try {
        // legacy 只送 deptId；雛形補帶 organizationId/departmentId（C5-h 接線，D4 登記）。
        await dialogs.processing.wrap(
          dataSource.update(pageId, anchorId, {
            organizationId,
            departmentId,
            deptId: departmentId,
            datas,
          }),
        );
        // :156-158 tip(data.msg)＋if(data.success) doQuery()——tip 無定義＋欄位不存在＝靜默（D8 照實）。
      } catch (cause: unknown) {
        // legacy 前端連失敗訊息都收不到（handler 拋 ReferenceError）；雛形錯誤 scenario 仍提示
        //（mock 錯誤語意，登記差異）。
        await dialogs.alert(cause instanceof MockDataError ? cause.message : TEXT_SAVE_ERROR);
      } finally {
        setBusy(false);
      }
    })();
  };

  const rowKey = (item: Row): string => String(item['id']).toLowerCase();

  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      {/* showProcessing 鏡像（doQuery/doSave 期間；busy 由 update/list 流程置位）。 */}
      <ProcessingOverlay uiId={`${pageId}-processing`} open={loading || busy} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title="主治醫師合理醫療服務量設定"
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        {/* 查詢列（:137-151）：*醫院＋*科部（級聯種子平列）。 */}
        <div data-ui-id={`${pageId}-query-form`} className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-right text-sm" htmlFor={`${pageId}-query-org`}>
              <span className="text-danger">*</span>醫院：
            </label>
            <Select
              uiId={`${pageId}-query-org`}
              name="organizationId"
              value={organizationId}
              onChange={(next) => {
                setOrganizationId(next);
                doQuery();
              }}
              options={overrideOptions('organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-right text-sm" htmlFor={`${pageId}-query-dept`}>
              <span className="text-danger">*</span>科部：
            </label>
            <Select
              uiId={`${pageId}-query-dept`}
              name="departmentId"
              value={departmentId}
              onChange={(next) => {
                setDepartmentId(next);
                doQuery(); // :26-27 departmentChangeEvent
              }}
              options={overrideOptions('departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS}
              // GNL-002（批次 29，issue #30）：首項鏡像 legacy 請選擇(-1)（service-standard-setting raw-dom）；
              // placeholder value="" 與 legacy -1 的語意差記錄結案。
              // 批次 52 修訂：override 命中（凍結 [共用(0),管理部門(1)]）即取代＋去 placeholder。
              {...(overrideOptions('departmentId') ? {} : { placeholder: '請選擇' })}
            />
          </div>
        </div>

        {/* 固定評估項目表（:152-156 #formTable 3 欄）。 */}
        <div className="mt-3">
          <Table
            uiId={`${pageId}-form-table`}
            columns={[
              { key: 'itemDesc1', header: '評估項目', align: 'center' },
              { key: 'aItemDesc1', header: '評估內容', align: 'center' },
              { key: 'aItemValue1', header: '本科標準', align: 'center' },
            ]}
            rows={SERVICE_STANDARD_ITEMS as readonly TableRow[]}
            rowKey={rowKey}
            emptyText="查無符合的資料"
            renderCell={(item, columnKey) => {
              const index = SERVICE_STANDARD_ITEMS.indexOf(item as Row);
              const value = deptValues[index] ?? EMPTY_DEPT_VALUE;
              if (columnKey === 'itemDesc1') {
                return String(item['itemDesc1'] ?? '');
              }
              if (columnKey === 'aItemDesc1') {
                // :73-82 評估內容 text input（N/A→disable，:62-63）。
                return (
                  <TextInput
                    uiId={`${pageId}-row-${rowKey(item)}-a-item-desc1`}
                    value={value.aItemDesc1}
                    disabled={value.naChecked}
                    onChange={(next) => {
                      patchValue(index, { aItemDesc1: next });
                    }}
                  />
                );
              }
              if (columnKey === 'aItemValue1') {
                // :83-102 本科標準：radio N/A（value 0）＋radio 數值（value 1 預設）＋number＋單位 addon。
                return (
                  <div className="inline-flex flex-wrap items-center gap-2">
                    <Radio
                      uiId={`${pageId}-row-${rowKey(item)}-chk-na`}
                      value="0"
                      checked={value.naChecked}
                      label="N/A"
                      onChange={(checked) => {
                        patchValue(index, { naChecked: checked }); // :55-68 valueRadioChange
                      }}
                    />
                    <Radio
                      uiId={`${pageId}-row-${rowKey(item)}-chk-value`}
                      value="1"
                      checked={!value.naChecked}
                      label=""
                      onChange={(checked) => {
                        patchValue(index, { naChecked: !checked });
                      }}
                    />
                    <input
                      data-ui-id={`${pageId}-row-${rowKey(item)}-a-item-value1`}
                      type="number"
                      className="w-24 border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                      value={value.aValue1}
                      disabled={value.naChecked}
                      onChange={(event) => {
                        patchValue(index, { aValue1: event.target.value });
                      }}
                    />
                    <span className="text-sm text-ink">{String(item['itemDesc2'] ?? '')}</span>
                  </div>
                );
              }
              return '\u00a0';
            }}
          />
        </div>

        {/* 存檔（:158-162 btn-primary onclick=doSave()）；成功後靜默（D8 照實）。 */}
        <div className="mt-3">
          <Button
            uiId={`${pageId}-actions-save`}
            variant="primary"
            size="sm"
            type="button"
            // C5-j：存檔真的呼叫 dataSource.update（endpoint-map update:… ✓）。
            // 不 disabled（transcript 先例）；成功後靜默（D8 照實）。
            dataEffect={`update:${pageId}`}
            onClick={doSave}
          >
            存檔
          </Button>
        </div>
      </ContentPanel>
    </div>
  );
}
